import { useEffect, useMemo, useRef, useState } from "react";
import {
  MapContainer,
  Marker,
  Polygon as LeafletPolygon,
  Polyline,
  Popup,
  TileLayer,
  useMap,
  useMapEvents,
} from "react-leaflet";
import L, { type LeafletMouseEvent } from "leaflet";
import "leaflet/dist/leaflet.css";
import { pointInPolygon, type LatLon } from "./geometry";
import { effectiveCarrierCode } from "./grouping";
import type { AssignmentMap, CarrierGroup, SaleRow } from "./types";

// ProgramacionMap — vista interactiva tipo Roadnet/RNA:
//   - Dibujo de polígono: click agrega vértice, doble-click cierra.
//   - Puntos adentro del polígono quedan "seleccionados" (halo).
//   - Drag desde un punto seleccionado a un punto de otro código →
//     reasigna todos los seleccionados al carrier destino.
//
// El estado de selección + assignments vive en el padre — este
// componente es controlado.

const LIMA_CENTER: [number, number] = [-12.0464, -77.0428];
// Sentinel para cuando `unassignedIds` no se pasa — evita instanciar
// un Set nuevo en cada render.
const EMPTY_SET: ReadonlySet<number> = new Set();

type Props = {
  rows: SaleRow[];
  carriers: CarrierGroup[];
  assignments: AssignmentMap;
  selectedIds: ReadonlySet<number>;
  /** IDs marcados como "no asignar" — render grises, no participan
   *  de drag/drop ni del optimize. */
  unassignedIds?: ReadonlySet<number>;
  /** true mientras la herramienta lasso esté activa (esperando mousedown). */
  drawingPolygon: boolean;
  /** Polígono cerrado actual (para mostrar de vuelta). null si vacío. */
  closedPolygon: LatLon[] | null;
  /** Vértices en construcción (en vivo mientras el usuario arrastra). */
  draftVertices: LatLon[];

  /** Lasso lifecycle — el padre los cablea a setState's. */
  onLassoStart: (v: LatLon) => void;
  onLassoPoint: (v: LatLon) => void;
  onLassoEnd: () => void;

  /** Reasignar: aplicar a TODOS los selectedIds. */
  onAssign: (targetCode: string) => void;
  /** Marcar selectedIds como "no asignar" — los saca del pool. */
  onMarkUnassigned?: () => void;

  /** Map rowId → sequence (orden de visita 1-indexed) que produce el
   *  optimizer OR-Tools. Si está vacío, no se muestran números. */
  sequenceByRowId?: Map<number, number>;
  /** Carrier seleccionado en la tabla — solo se numeran sus paradas. */
  focusCode?: string | null;
};

export function ProgramacionMap(props: Props) {
  const { rows, carriers, assignments, selectedIds } = props;

  // Index: rowId → color para markers + lookup rápido.
  const rowById = useMemo(() => {
    const m = new Map<number, SaleRow>();
    for (const r of rows) m.set(r.id, r);
    return m;
  }, [rows]);

  const colorByCode = useMemo(() => {
    const m = new Map<string, string>();
    for (const c of carriers) m.set(c.code, c.color);
    return m;
  }, [carriers]);

  const initialCenter = useMemo<[number, number]>(() => {
    if (rows.length === 0) return LIMA_CENTER;
    let lat = 0, lon = 0, n = 0;
    for (const r of rows) {
      if (Number.isFinite(r.lat) && Number.isFinite(r.lon)) {
        lat += r.lat;
        lon += r.lon;
        n++;
      }
    }
    return n === 0 ? LIMA_CENTER : [lat / n, lon / n];
  }, [rows]);

  return (
    <MapContainer
      center={initialCenter}
      zoom={12}
      minZoom={5}
      maxZoom={22}
      scrollWheelZoom
      doubleClickZoom={!props.drawingPolygon}
      style={{ height: "100%", width: "100%" }}
    >
      <TileLayer
        attribution='&copy; <a href="https://www.openstreetmap.org/">OpenStreetMap</a> contributors'
        url="https://tile.openstreetmap.org/{z}/{x}/{y}.png"
        maxZoom={22}
        maxNativeZoom={19}
      />

      <FitOnFirstLoad rows={rows} />
      <LassoHandlers
        active={props.drawingPolygon}
        onStart={props.onLassoStart}
        onPoint={props.onLassoPoint}
        onEnd={props.onLassoEnd}
      />

      {/* Polígono en draft (en vivo) — relleno semi-transparente para
          que el usuario vea el área que está rodeando. Si solo hay
          1-2 puntos lo mostramos como línea. */}
      {props.drawingPolygon && props.draftVertices.length >= 3 && (
        <LeafletPolygon
          positions={props.draftVertices}
          pathOptions={{
            color: "#FB8C00",
            weight: 2,
            fillColor: "#FB8C00",
            fillOpacity: 0.18,
            dashArray: "5 4",
          }}
        />
      )}
      {props.drawingPolygon && props.draftVertices.length > 0 && props.draftVertices.length < 3 && (
        <Polyline
          positions={props.draftVertices}
          pathOptions={{ color: "#FB8C00", weight: 2, dashArray: "5 4" }}
        />
      )}

      {/* Polígono cerrado: relleno claro + borde */}
      {props.closedPolygon && (
        <LeafletPolygon
          positions={props.closedPolygon}
          pathOptions={{
            color: "#1565C0",
            weight: 2,
            fillColor: "#1565C0",
            fillOpacity: 0.08,
          }}
        />
      )}

      {/* Floating chip en el centroide del polígono cerrado: aparece
          cuando hay puntos seleccionados y el polígono ya quedó cerrado.
          Da acciones rápidas (no asignar / cerrar) sin volver al
          toolbar superior — UX contextual. */}
      {props.closedPolygon && props.selectedIds.size > 0 && props.onMarkUnassigned && (
        <LassoActionChip
          polygon={props.closedPolygon}
          count={props.selectedIds.size}
          onMarkUnassigned={props.onMarkUnassigned}
        />
      )}

      <MarkerLayer
        rows={rows}
        rowById={rowById}
        assignments={assignments}
        colorByCode={colorByCode}
        selectedIds={selectedIds}
        unassignedIds={props.unassignedIds ?? EMPTY_SET}
        onAssign={props.onAssign}
        sequenceByRowId={filterSequenceForFocus(props.sequenceByRowId, props.focusCode, rowById, assignments)}
        focusCode={props.focusCode ?? null}
      />
    </MapContainer>
  );
}

/// Si hay focusCode, deja solo las sequences del carrier focuseado. Si
/// no, deja todas. Los markers de carriers no-focuseados no mostrarán
/// número, lo que reduce el clutter visual.
function filterSequenceForFocus(
  full: Map<number, number> | undefined,
  focusCode: string | null | undefined,
  rowById: Map<number, SaleRow>,
  assignments: AssignmentMap,
): Map<number, number> {
  if (!full || full.size === 0) return new Map();
  if (!focusCode) return full;
  const out = new Map<number, number>();
  for (const [rowId, seq] of full) {
    const r = rowById.get(rowId);
    if (!r) continue;
    const code = effectiveCarrierCode(r, assignments);
    if (code === focusCode) out.set(rowId, seq);
  }
  return out;
}

// ---------------- internals ----------------

function FitOnFirstLoad({ rows }: { rows: SaleRow[] }) {
  const map = useMap();
  const fittedRef = useRef(false);
  useEffect(() => {
    if (fittedRef.current) return;
    const pts = rows.filter((r) => Number.isFinite(r.lat) && Number.isFinite(r.lon));
    if (pts.length === 0) return;
    const bounds = L.latLngBounds(pts.map((r) => [r.lat, r.lon] as L.LatLngTuple));
    map.fitBounds(bounds, { padding: [40, 40] });
    fittedRef.current = true;
  }, [rows, map]);
  return null;
}

/// LassoHandlers — dibujo freehand del polígono.
///   - mousedown sobre el mapa (no marker): empieza el lasso, primer vértice.
///   - mousemove con botón apretado: agrega vértices según el cursor se
///     mueve, con un threshold de distancia para no inundar el array.
///   - mouseup: cierra el polígono.
///
/// Durante el lasso desactivamos el pan del mapa para que arrastrar no
/// mueva la vista. Lo re-activamos al terminar (o si el usuario
/// cancela con ESC desde el padre).
function LassoHandlers({
  active,
  onStart,
  onPoint,
  onEnd,
}: {
  active: boolean;
  onStart: (v: LatLon) => void;
  onPoint: (v: LatLon) => void;
  onEnd: () => void;
}) {
  const map = useMap();
  const drawingRef = useRef(false);
  const lastPointRef = useRef<L.Point | null>(null);

  // Threshold: mínimo 6 pixels de pantalla entre puntos consecutivos
  // — evita guardar 500 puntos por un drag de 10 cm. Trabajamos en
  // pixeles (no grados) porque a distintos zooms 0.0005° puede ser
  // muchísimo o nada.
  const MIN_PX_BETWEEN_POINTS = 6;

  useMapEvents({
    mousedown(e: LeafletMouseEvent) {
      if (!active) return;
      // Solo arrancamos si el evento es sobre el "background" — Leaflet
      // ya filtra esto: mousedown sobre un marker emite el evento del
      // marker (que captura stopPropagation arriba) y NO llega acá.
      L.DomEvent.preventDefault(e.originalEvent);
      drawingRef.current = true;
      map.dragging.disable();
      map.doubleClickZoom.disable();
      lastPointRef.current = e.containerPoint;
      onStart([e.latlng.lat, e.latlng.lng]);
    },
    mousemove(e: LeafletMouseEvent) {
      if (!drawingRef.current) return;
      const last = lastPointRef.current;
      if (last && e.containerPoint.distanceTo(last) < MIN_PX_BETWEEN_POINTS) return;
      lastPointRef.current = e.containerPoint;
      onPoint([e.latlng.lat, e.latlng.lng]);
    },
    mouseup() {
      if (!drawingRef.current) return;
      drawingRef.current = false;
      lastPointRef.current = null;
      map.dragging.enable();
      map.doubleClickZoom.enable();
      onEnd();
    },
  });

  // Cuando el caller desactiva el modo lasso (ESC, "Cancelar"), si
  // estábamos en medio de un dibujo limpiar el estado interno.
  useEffect(() => {
    if (!active && drawingRef.current) {
      drawingRef.current = false;
      lastPointRef.current = null;
      map.dragging.enable();
      map.doubleClickZoom.enable();
    }
  }, [active, map]);

  return null;
}

function MarkerLayer({
  rows,
  rowById,
  assignments,
  colorByCode,
  selectedIds,
  unassignedIds,
  onAssign,
  sequenceByRowId,
  focusCode,
}: {
  rows: SaleRow[];
  rowById: Map<number, SaleRow>;
  assignments: AssignmentMap;
  colorByCode: Map<string, string>;
  selectedIds: ReadonlySet<number>;
  unassignedIds: ReadonlySet<number>;
  onAssign: (targetCode: string) => void;
  sequenceByRowId: Map<number, number>;
  focusCode: string | null;
}) {
  const map = useMap();
  const [dragFromId, setDragFromId] = useState<number | null>(null);
  const [dragCursor, setDragCursor] = useState<LatLon | null>(null);
  // ID del marker que el mouse está pisando AHORA mientras arrastra.
  // Solo este recibe el ring de "drop válido" — no pintamos todos los
  // de la pantalla como candidatos (era ruido visual).
  const [hoverTargetId, setHoverTargetId] = useState<number | null>(null);

  const startDrag = (rowId: number, latlng: LatLon) => {
    if (!selectedIds.has(rowId)) return;
    map.dragging.disable();
    map.doubleClickZoom.disable();
    setDragFromId(rowId);
    setDragCursor(latlng);
    setHoverTargetId(null);
  };
  const cancelDrag = () => {
    map.dragging.enable();
    map.doubleClickZoom.enable();
    setDragFromId(null);
    setDragCursor(null);
    setHoverTargetId(null);
  };
  const finishDragOn = (targetRowId: number) => {
    if (dragFromId === null) return;
    const targetRow = rowById.get(targetRowId);
    if (!targetRow) return cancelDrag();
    // No reasignar si el target es uno de los seleccionados, o si
    // tiene el mismo código que el carrier origen.
    if (selectedIds.has(targetRowId)) return cancelDrag();
    const targetCode = effectiveCarrierCode(targetRow, assignments);
    const sourceRow = rowById.get(dragFromId);
    const sourceCode = sourceRow ? effectiveCarrierCode(sourceRow, assignments) : "";
    if (targetCode === sourceCode) return cancelDrag();
    onAssign(targetCode);
    cancelDrag();
  };

  useMapEvents({
    mousemove(e: LeafletMouseEvent) {
      if (dragFromId === null) return;
      setDragCursor([e.latlng.lat, e.latlng.lng]);
    },
    mouseup() {
      // Mouseup en el mapa (no en un marker) → cancelar.
      if (dragFromId !== null) cancelDrag();
    },
  });

  return (
    <>
      {rows.map((r) => {
        if (!Number.isFinite(r.lat) || !Number.isFinite(r.lon)) return null;
        const isUnassigned = unassignedIds.has(r.id);
        const code = effectiveCarrierCode(r, assignments);
        // Filtro por focusCode: si hay carrier seleccionado en la
        // tabla, ocultamos los puntos de los demás (UX "ver solo la
        // ruta del chofer seleccionado"). Los unassigned igual se
        // ocultan en modo focus — no pertenecen a ningún carrier.
        if (focusCode && (isUnassigned || code !== focusCode)) return null;
        const color = isUnassigned
          ? "#B0B7BD" // gris neutral para no asignados
          : colorByCode.get(code) ?? "#90A4AE";
        const selected = selectedIds.has(r.id);
        // Solo destacamos el marker que el cursor está pisando AHORA,
        // y nada más — los demás se quedan en su estado neutro aunque
        // sean teóricamente "drop válido".
        const isHoverTarget =
          dragFromId !== null && hoverTargetId === r.id && !selected;
        const seq = sequenceByRowId.get(r.id);
        return (
          <Marker
            key={r.id}
            position={[r.lat, r.lon]}
            icon={dotIcon(color, selected, isHoverTarget, seq, isUnassigned)}
            eventHandlers={{
              mousedown: (e: L.LeafletMouseEvent) => {
                // Los unassigned NO pueden ser dragged ni participan
                // del flujo de reasignación.
                if (selected && !isUnassigned) {
                  L.DomEvent.stopPropagation(e.originalEvent);
                  startDrag(r.id, [r.lat, r.lon]);
                }
              },
              mouseover: () => {
                if (dragFromId !== null && !selected && !isUnassigned) setHoverTargetId(r.id);
              },
              mouseout: () => {
                if (hoverTargetId === r.id) setHoverTargetId(null);
              },
              mouseup: (e: L.LeafletMouseEvent) => {
                if (dragFromId !== null && !isUnassigned) {
                  L.DomEvent.stopPropagation(e.originalEvent);
                  finishDragOn(r.id);
                }
              },
            }}
          >
            <Popup>
              <div style={{ minWidth: 160 }}>
                <strong style={{ fontSize: 13 }}>{r.cliente}</strong>
                <div style={{ fontSize: 11, color: "#5C6B7A", marginTop: 4 }}>
                  Código: {code || "—"}
                </div>
                <div style={{ fontSize: 12, marginTop: 2 }}>
                  Venta: S/ {r.valorVenta.toFixed(2)}
                </div>
              </div>
            </Popup>
          </Marker>
        );
      })}

      {dragFromId !== null && dragCursor && rowById.get(dragFromId) && (
        <Polyline
          positions={[
            [rowById.get(dragFromId)!.lat, rowById.get(dragFromId)!.lon],
            dragCursor,
          ]}
          pathOptions={{ color: "#FB8C00", weight: 2.5, dashArray: "5 5" }}
        />
      )}
    </>
  );
}

function dotIcon(
  color: string,
  selected: boolean,
  isPotentialTarget: boolean,
  sequence?: number,
  isUnassigned: boolean = false,
): L.DivIcon {
  // Si hay sequence (post-optimize) usamos un dot más grande con número
  // adentro. Sino, el dot chico habitual.
  const hasSeq = sequence != null && sequence > 0;
  const size = hasSeq ? 22 : selected ? 16 : isPotentialTarget ? 14 : 12;
  const ring = selected
    ? `box-shadow:0 0 0 3px rgba(251,140,0,0.55);`
    : isPotentialTarget
      ? `box-shadow:0 0 0 2px rgba(21,101,192,0.45);`
      : "";
  const inner = hasSeq
    ? `<span style="color:white;font-size:10px;font-weight:700;font-family:system-ui,sans-serif;line-height:1;">${sequence}</span>`
    : "";
  // Los unassigned se ven dim + con border discontinuo para que el
  // admin entienda "este existe pero está excluido del pool".
  const opacity = isUnassigned ? 0.4 : 1;
  const border = isUnassigned
    ? "border:1.5px dashed #5C6B7A;"
    : "border:1.5px solid white;";
  return L.divIcon({
    className: "",
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
    html: `<div style="
      width:${size}px;height:${size}px;border-radius:50%;
      background:${color};
      ${border}
      opacity:${opacity};
      display:flex;align-items:center;justify-content:center;
      ${ring}
      cursor:${selected ? "grab" : isUnassigned ? "default" : "pointer"};
    ">${inner}</div>`,
  });
}

/// LassoActionChip — burbuja flotante anclada al centroide del polígono
/// cerrado. Aparece cuando hay selección activa y ofrece acciones
/// contextuales sin obligar al usuario a moverse al toolbar superior.
function LassoActionChip({
  polygon,
  count,
  onMarkUnassigned,
}: {
  polygon: LatLon[];
  count: number;
  onMarkUnassigned: () => void;
}) {
  const centroid = polygonCentroid(polygon);
  if (!centroid) return null;
  // Marker transparente + Popup auto-open. El Popup tiene autoClose=false
  // así el click del usuario en el botón no lo dispara.
  return (
    <Marker
      position={centroid}
      icon={transparentPin()}
      eventHandlers={{ add: (e) => (e.target as L.Marker).openPopup() }}
    >
      <Popup autoClose={false} closeOnClick={false} closeButton={false} offset={[0, -4]}>
        <div style={{ minWidth: 180, padding: "2px 4px" }}>
          <div style={{ fontSize: 12, color: "#243B53", marginBottom: 6 }}>
            <strong>{count}</strong> punto{count === 1 ? "" : "s"} seleccionado
            {count === 1 ? "" : "s"}
          </div>
          <button
            onClick={onMarkUnassigned}
            style={{
              display: "block",
              width: "100%",
              padding: "6px 8px",
              background: "#C62828",
              color: "white",
              border: "none",
              borderRadius: 6,
              fontSize: 12,
              fontWeight: 600,
              cursor: "pointer",
            }}
          >
            ✗ No asignar
          </button>
          <div style={{ fontSize: 10, color: "#5C6B7A", marginTop: 6, lineHeight: 1.3 }}>
            O arrastrá un punto a otro color para reasignarlos.
          </div>
        </div>
      </Popup>
    </Marker>
  );
}

function transparentPin(): L.DivIcon {
  return L.divIcon({
    className: "",
    iconSize: [1, 1],
    iconAnchor: [0, 0],
    html: '<div style="width:1px;height:1px;"></div>',
  });
}

function polygonCentroid(poly: LatLon[]): LatLon | null {
  if (poly.length === 0) return null;
  let lat = 0;
  let lon = 0;
  for (const [la, lo] of poly) {
    lat += la;
    lon += lo;
  }
  return [lat / poly.length, lon / poly.length];
}

/// `selectByPolygon` — helper que el padre llama tras cerrar polígono.
/// Devuelve el Set de IDs de rows que caen adentro. Pura, testeable.
export function selectByPolygon(
  rows: SaleRow[],
  polygon: LatLon[],
): Set<number> {
  const out = new Set<number>();
  if (polygon.length < 3) return out;
  for (const r of rows) {
    if (!Number.isFinite(r.lat) || !Number.isFinite(r.lon)) continue;
    if (pointInPolygon([r.lat, r.lon], polygon)) out.add(r.id);
  }
  return out;
}
